import { v4 as uuidv4 } from "uuid";
import { states } from "@/assets/data/us_states";
import i18n from "@/i18n";
import { parseToHtml } from "@/utility/parseToHtml";
import { decodeHtmlSymbols } from "@/utility/htmlEscape";
import { LIVE_CHAT_MP4_ROOT_DEFAULTS } from "@/components/shortcodes/livechatMp4/constant";
import { getShortcodeWrapper, getSpanShortcodeWrapper, shortcodesList } from "../shortodeAddition";
import { getCustomButtonView } from "../shortcodeConfigs/customButtonConfig";
import {
  getAgeCTA3Template,
  getAgeCTA4Template,
  getAgeInputQuizTemplate,
  getAnimatedButtonTemplate,
  getBottomBarTemplate,
  getButtonsWithTextBoxTemplate,
  getCTAIndyTemplate,
  getCTAOuterTemplate,
  getCTAPhoneTemplate,
  getCTATemplate,
  getCTATemplateWidthAd,
  getCarrierNamesOtcTemplate,
  getCarrierNamesPartBTemplate,
  getCheckoutFormTemplate,
  getCheckoutPlanTemplate,
  getCommentsTemplate,
  getContactFormTemplate,
  getContactUsFormTemplate,
  getContentBoxTemplate,
  getContentSplitTemplate,
  getCountdownTemplate,
  getCounterTemplate,
  getCtaButtonNumberTemplate,
  getCustomButtonTemplate,
  getDripFormTemplate,
  getDynamicButtonsTemplate,
  getDynamicColorMultiButtonCta,
  getDynamicHighlightBoxTemplate,
  getEmailFormTemplate,
  getGifAgeTemplate,
  getHeadlineWithBgTemplate,
  getImageBannerTemplate,
  getLiveChatAutoTemplate,
  getLiveChatCTATemplate,
  getLiveChatCTAV2Template,
  getLiveChatMp4Template,
  getLiveChatMp4V2Template,
  getLiveChatTemplate,
  getLiveChatV2Template,
  getLiveClaimFeedTemplate,
  getMileageTemplate,
  getMoneyCTATemplate,
  getMultiButtonCta,
  getMultiButtonCtaV3Template,
  getMultiButtonCtaV4Template,
  getMultiButtonCtaWithImages,
  getMultiStepFormTemplate,
  getMultiStepQuizTemplate,
  getMultiStepQuizV2Template,
  getMultiStepQuizV3Template,
  getMultiStepQuizV4Template,
  getMultipleCTATemplate,
  getNewsletterBannerTemplate,
  getNumberedStepsTemplate,
  getQuoteBoxTemplate,
  getQuoteTemplate,
  getReviewRatingsTemplate,
  getRingbaPhoneNumberTemplate,
  getSelectAgeIconsTemplate,
  getSelectAgeInOneRowTemplate,
  getSelectAgeTemplate,
  getShortCTA4Template,
  getShortCTATemplate,
  getShortcodeHTMLTemplate,
  getSkinnyAgeButtonsTemplate,
  getSmallTextBoxTemplate,
  getSocialProofTemplate,
  getSocialProofV2Template,
  getSocialSharerTemplate,
  getSpinWheelTemplate,
  getTeamMembersTemplate,
  getTextHighlightTemplate,
  getTextUnderlineTemplate,
  getTimerTemplate,
  getToastTemplate,
  getUsStatesShortcodeTemplate,
  getWarningBannerTemplate,
  getWhiteMultiButtonCta,
  getZipCodeTemplate,
} from "./templates";

const ELEMENT_CATEGORIES = [
  "cta",
  "call",
  "qualifier",
  "quizzes",
  "forms",
  "live_chat",
  "social_proof",
  "banners",
  "text",
  "utility",
];

const UNCATEGORIZED = "other";

/**
 * The editor keeps every element inside a `.shortcode` wrapper, and `data-wrapper-id` is what
 * tells the config dialogs and the API serializer which element it holds. Most elements are
 * inserted without one; these are the exceptions, plus the few that use a `<span>` wrapper or
 * no wrapper at all. Anything pasted without this structure is plain HTML the editor cannot
 * open for editing again.
 */
const EDITOR_WRAPPERS = {
  "Age Quiz": { id: "age-input-quiz" },
  "Bottom Bar": { id: "bottom-bar-shortcode" },
  "Buttons With Text-Box": { id: "buttons-with-text-box-shortcode" },
  "Carrier Names (OTC)": { id: "carrier-names", inline: true },
  "Carrier Names (Part B)": { id: "carrier-names", inline: true },
  // A container element: other design elements live inside its columns, so it is never wrapped.
  "Content Box": { unwrapped: true },
  "Content Split": { id: "content-split" },
  "Contact Us Form": { id: "contact-us-form" },
  Countdown: { id: "countdown-shortcode" },
  Counter: { id: "counter", inline: true },
  "Dynamic Buttons": { id: "dynamic-buttons" },
  "Image Banner": { id: "image-banner" },
  "Live Chat": { id: "live-chat-shortcode" },
  "Live Chat (MP4)": { id: "live-chat-mp4-shortcode" },
  "Live Chat (MP4) V2": { id: "live-chat-mp4-v2-shortcode" },
  "Live Chat - Auto": { id: "live-chat-auto-shortcode" },
  "Live Chat V2": { id: "live-chat-v2-shortcode" },
  "Live Claim Feed": { id: "live-claim-feed" },
  "Multi-Step Form": { id: "multi-step-form-shortcode" },
  "Multi-Step Quiz #1": { id: "multi-step-quiz-v1" },
  "Multi-Step Quiz #2": { id: "multi-step-quiz-v2" },
  "Multi-Step Quiz #3": { id: "multi-step-quiz-v3" },
  "Multi-Step Quiz #4": { id: "multi-step-quiz-v4-shortcode" },
  "Newsletter Banner": { id: "newsletter-banner" },
  "Numbered Steps": { id: "numbered-steps" },
  "Ringba Phone Number": { id: "ringba-phone-number" },
  "Social Proof": { id: "social-proof" },
  "Social Proof V2": { id: "social-proof-v2" },
  "Spin wheel": { id: "spin-wheel" },
  "Text Underline": { id: "text-underline" },
  Timer: { id: "timer", inline: true },
  Toast: { id: "toast-shortcode" },
  "Zip Code": { id: "zip-code-shortcode" },
};

/**
 * What each element exposes and the values each option accepts, taken from the option keys its
 * template reads, the data model of its config dialog, and the choices its dialog offers.
 * Elements whose config is a JSON payload also record the attribute holding it and how it is
 * encoded. Anything missing here has no options: its text is edited directly in the page content.
 */
/**
 * Four encodings are in use across the config dialogs. They round-trip identically for plain
 * ASCII, which is why they can be mistaken for each other, but they diverge the moment the copy
 * carries an accent, an em dash or a percent sign - so each element names the exact expression
 * that produces its attribute value.
 */
const ENCODINGS = {
  escaped: "btoa(escape(encodeURIComponent(JSON.stringify(config))))",
  unescaped: "btoa(unescape(encodeURIComponent(JSON.stringify(config))))",
  base64: "btoa(JSON.stringify(config))",
  json: "JSON.stringify(config)",
  // The value lands in a single-quoted attribute, so the apostrophe escape is load-bearing.
  htmlEscapedJson: `JSON.stringify(config).replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/'/g, "&#39;")`,
};

const TAB = '"_self" | "_blank"';
const ALIGNMENT = '"left" | "center" | "right"';
const GRID_LAYOUT = '"3x2" | "3x3" | "3x4" | "custom"';
const COLOR = "color (hex)";
const HTML = "html";
const PX = "number (px)";

/**
 * The chat flow is a tree, and one sample only shows the couple of node types it happens to use.
 * These are every node the chat builder can produce, from `shortcodes/livechat/constant.js`, so a
 * flow can be written without having seen the builder. Each node carries `messageType` naming its
 * type and `children` holding what comes next; `messages[0]` is where the conversation starts.
 */
const LIVE_CHAT_NODE_TYPES = {
  message: {
    content: HTML,
    delay: "number (ms before it appears)",
    human: "boolean (true renders it as the visitor)",
    backgroundColor: COLOR,
    hideBackground: "boolean",
    hideLoader: "boolean",
    fullWidth: "boolean",
    isLastMessage: "boolean",
    children: "node[]",
  },
  action: {
    delay: "number (ms)",
    columns: "number | null (buttons per row)",
    backgroundColor: COLOR,
    floatingText: "{ enabled, text, animation }",
    children: "actionItem[]",
  },
  actionItem: {
    text: "string (button label)",
    value: "string (recorded answer)",
    backgroundColor: COLOR,
    ringbaTags: "object[]",
    children: "node[] (what this answer leads to)",
  },
  inputQuestion: {
    content: HTML,
    inputQuestionType: '"enter_age" | "enter_birth_year" | "enter_birth_year_underscore" | "enter_zip"',
    inputQuestionDisplayType: '"years" | "age" (how the answer is echoed back)',
    buttonText: "string",
    buttonColor: COLOR,
    displayUserAnswer: "boolean",
    children: "node[]",
  },
  zipInputQuestion: {
    content: HTML,
    zipInputDisplayType: '"empty" | "prefilled"',
    buttonText: "string",
    placeholderText: "string",
    overrideZipOnly: "boolean",
    overrideZipAndLocation: "boolean",
    children: "node[]",
  },
  defaultQuestion: {
    content: HTML,
    defaultQuestionType: '"age_group"',
    delay: "number (ms)",
    children: "node[]",
  },
  decision: {
    greaterThan: "number | null",
    greaterThanOrEqual: "number | null",
    lessThan: "number | null",
    lessThanOrEqual: "number | null",
    ringbaTags: "object[]",
    children: "node[] (branch taken when the answer matches)",
  },
};

const ELEMENT_CONTROLS = {
  "Age CTA #1": { controls: { url: "url", tab: TAB, type: "colour keyword, e.g. green" } },
  "Age CTA #2": {
    controls: {
      url: "url",
      tab: TAB,
      count: '"3" | "4"',
      bgColor: COLOR,
      hoverColor: COLOR,
      cornerRadius: PX,
      buttonSpacing: PX,
    },
  },
  "Age CTA #3": {
    controls: { url: "url", tab: TAB, count: '"3" | "4"', bgColor: COLOR, hoverColor: COLOR, cornerRadius: PX },
  },
  "Age CTA #4": { controls: { url: "url", tab: TAB, version: "1 | 2 | 3 | 4" } },
  "Age Gif": { controls: { url: "url", tab: TAB } },
  "Age Quiz": {
    attribute: "data-age-input-data",
    encoding: ENCODINGS.escaped,
    controls: {
      ageInputType: '"age"',
      phoneNumber: "string",
      showSpapQuestion: "boolean",
      audioEnabled: "boolean",
      initialPageAudio: "audio file object",
      thankYouPageAudio: "audio file object",
      "questions[]": "object[]",
    },
  },
  "Animated Button": {
    controls: { url: "url", tab: TAB, text: "string", type: '"link" | "call"', bgColor: COLOR, hoverColor: COLOR },
  },
  "Bottom Bar": {
    attribute: "data-bottom-bar-data",
    encoding: ENCODINGS.unescaped,
    controls: { phoneNumber: "string", phoneNumberAppendText: "string" },
  },
  "Button CTA #1": { controls: { url: "url", tab: TAB, text: "string", bgColor: COLOR } },
  "Button CTA #2": { controls: { url: "url", tab: TAB, text: "string", type: "colour keyword, e.g. green" } },
  "Button CTA #3": { controls: { url: "url", tab: TAB, text: "string", bgColor: COLOR } },
  "Button CTA #4": { controls: { url: "url", tab: TAB, text: "string", bgColor: COLOR, textColor: COLOR } },
  "Button CTA (With Ad)": { controls: { url: "url", tab: TAB, text: "string" } },
  "Buttons With Text-Box": {
    attribute: "data-buttons-with-text-box-data",
    encoding: ENCODINGS.base64,
    controls: {
      primaryColor: COLOR,
      "buttons[].title": "string",
      "buttons[].type": '"url" | "textBox"',
      "buttons[].text": HTML,
      "buttons[].url": "url",
      "buttons[].openIn": TAB,
      "buttons[].textBgColor": COLOR,
      "buttons[].textBorderColor": COLOR,
    },
  },
  "CTA Outer Button": { controls: { url: "url", tab: TAB, text: "string" } },
  "Call CTA #1": { controls: { number: "phone number", text: "string", bgColor: COLOR } },
  "Call CTA #2": {
    controls: {
      number: "phone number",
      text: "string",
      url: "phone number or url",
      tab: TAB,
      hrefType: '"tel:" | ""',
      bgColor: COLOR,
      borderColor: COLOR,
    },
  },
  "Comments Widget": {
    attribute: "data-comments-widget-data",
    encoding: ENCODINGS.json,
    controls: { "comments[]": "object[]" },
  },
  "Contact Us Form": {
    controls: {
      sidebarTitle: "string",
      sidebarSubtitle: "string",
      phone: "phone number",
      email: "email",
      address: "string",
      company: "string",
      buttonText: "string",
      buttonBgColor: COLOR,
      buttonTextColor: COLOR,
      showSideBlock: "boolean",
      sideBlockBgColor: COLOR,
      sideBlockTextColor: COLOR,
      sendEmailToGroove: "boolean",
      successMessage: "string",
      errorMessage: "string",
    },
  },
  "Content Box": {
    attribute: "data-content-box-data",
    encoding: ENCODINGS.escaped,
    controls: {
      borderSides: "{ top, right, bottom, left: boolean }",
      borderColor: COLOR,
      borderWidth: PX,
      cornerRadius: PX,
      enableBackground: "boolean",
      backgroundColor: COLOR,
      margin: PX,
      enableMaxWidth: "boolean",
      maxWidth: PX,
      fullWidth: "boolean",
      separateMobile: "boolean",
      mobile: "same keys again, applied under 768px",
    },
  },
  "Content Split": {
    attribute: "content-split-data",
    encoding: ENCODINGS.base64,
    controls: {
      countries: "string[]",
      states: "string[]",
      counties: "string[]",
      cities: "string[]",
      enableUTMParameters: "boolean",
    },
  },
  Countdown: {
    attribute: "data-countdown-data",
    encoding: ENCODINGS.unescaped,
    controls: { selectedDate: "date string", timerColor: COLOR },
  },
  Counter: { controls: { quantity: "number (start value)" } },
  "Custom Button": {
    attribute: "data-custom-button-data",
    encoding: ENCODINGS.unescaped,
    controls: {
      text: "string",
      enableSecondLine: "boolean",
      secondaryText: "string",
      secondaryFontSize: PX,
      secondaryTextColor: COLOR,
      lineGap: PX,
      enableIcon: "boolean",
      icon: '"check" | "arrow" | "phone" | "custom"',
      customIconId: "media id",
      customIconSrc: "url",
      iconGap: PX,
      iconSize: PX,
      iconColor: COLOR,
      url: "url",
      openLink: TAB,
      bgColor: COLOR,
      textColor: COLOR,
      enableHover: "boolean",
      hoverColor: COLOR,
      enableBorder: "boolean",
      borderColor: COLOR,
      borderWidth: PX,
      borderRadius: PX,
      fontFamily: "string (css font stack)",
      fontSize: PX,
      enableNoWrap: "boolean",
      paddingY: PX,
      paddingX: PX,
      enableFullWidth: "boolean",
      width: PX,
      enablePulse: "boolean",
      enableAd: "boolean",
      enable3d: "boolean",
      shadowX: PX,
      shadowY: PX,
      shadowBlur: PX,
      shadowColor: COLOR,
    },
  },
  "Drip Form Widget": { controls: { title: "string", subtitle: "string", buttonText: "string" } },
  "Dynamic Buttons": {
    attribute: "data-dynamic-buttons-data",
    encoding: ENCODINGS.escaped,
    controls: {
      globalLink: "url",
      mobileColumns: "number",
      desktopColumns: "number",
      "buttons[].text": "string",
      "buttons[].link": "url",
      "buttons[].target": TAB,
      "buttons[].backgroundColor": COLOR,
      "buttons[].textColor": COLOR,
      "buttons[].shadowColor": COLOR,
    },
  },
  "Headline #1": {
    controls: {
      text: HTML,
      enableBorders: "boolean",
      bordersColor: COLOR,
      enableBackgroundColor: "boolean",
      bgColor: COLOR,
    },
  },
  "Image Banner": {
    attribute: "data-image-banner-data",
    encoding: ENCODINGS.unescaped,
    controls: {
      desktopGridLayout: GRID_LAYOUT,
      desktopCustomLayout: "string, e.g. 2x4",
      mobileGridLayout: GRID_LAYOUT,
      mobileCustomLayout: "string, e.g. 2x4",
      "imageBanners[]": "{ imageID, imageURL, headline, subHeadline, url }[]",
    },
  },
  "Live Chat": {
    attribute: "data-live-chat-data",
    encoding: ENCODINGS.unescaped,
    nodeTypes: LIVE_CHAT_NODE_TYPES,
    controls: {
      botName: "string",
      botMessageBackgroundColor: COLOR,
      actionItemBackgroundColor: COLOR,
      userMessageBackgroundColor: COLOR,
      userMessageTextColor: COLOR,
      animatedButton: "boolean",
      theme: "string",
      counterBottom: "object",
      "messages[]": "tree of nodeTypes; messages[0] is the first node",
    },
  },
  "Live Chat (MP4)": {
    attribute: "data-live-chat-mp4-data",
    encoding: ENCODINGS.unescaped,
    nodeTypes: LIVE_CHAT_NODE_TYPES,
    controls: { "messages[]": "tree of nodeTypes; messages[0] is the first node" },
  },
  "Live Chat (MP4) V2": {
    attribute: "data-live-chat-mp4-data",
    encoding: ENCODINGS.unescaped,
    nodeTypes: LIVE_CHAT_NODE_TYPES,
    controls: { "messages[]": "tree of nodeTypes; messages[0] is the first node" },
  },
  "Live Chat - Auto": {
    attribute: "data-live-chat-auto-data",
    encoding: ENCODINGS.unescaped,
    nodeTypes: LIVE_CHAT_NODE_TYPES,
    controls: {
      botName: "string",
      actionItemBackgroundColor: COLOR,
      userMessageBackgroundColor: COLOR,
      "messages[]": "tree of nodeTypes; messages[0] is the first node",
    },
  },
  "Live Chat V2": {
    attribute: "data-live-chat-data",
    encoding: ENCODINGS.unescaped,
    nodeTypes: LIVE_CHAT_NODE_TYPES,
    controls: {
      botName: "string",
      botMessageBackgroundColor: COLOR,
      actionItemBackgroundColor: COLOR,
      userMessageBackgroundColor: COLOR,
      userMessageTextColor: COLOR,
      "messages[]": "tree of nodeTypes; messages[0] is the first node",
    },
  },
  "Live Claim Feed": {
    attribute: "data-live-claim-feed-data",
    encoding: ENCODINGS.escaped,
    controls: {
      feedTitle: "string",
      turnRate: "number (ms)",
      numberOfLines: "number",
      numberOfSections: "number",
      numberOfUsers: "number",
      "claimFeedUsers[]": "object[]",
    },
  },
  "Livechat CTA #1": {
    controls: {
      url: "url",
      text: "string",
      bgColor: COLOR,
      pulsingAnimation: "boolean",
      spaceBellow: "boolean",
    },
  },
  "Livechat CTA #2": {
    attribute: "live-chat-cta-v2-data",
    encoding: ENCODINGS.unescaped,
    controls: {
      url: "url",
      twoLine: "boolean",
      line1: "string",
      line2: "string",
      bgColor: COLOR,
      textColor: COLOR,
      fontFamily: "font name",
      fontSize: PX,
      fontSize2: PX,
      iconSrc: "url",
      pulsingAnimation: "boolean",
      spaceBellow: "boolean",
    },
  },
  "Multi Button CTA (White)": { controls: { url: "url", tab: TAB, quantity: '"4" | "5"' } },
  "Multi Button CTA with Images": {
    controls: {
      quantity: "3 | 5",
      "data.customUrl": "url",
      "data.openLink": TAB,
      "data.imgSrc<n>": "media object",
      "data.imgAlt<n>": "string",
      "data.boxInput<n>": "string",
    },
  },
  "Multi-Button CTA #1": { controls: { quantity: '"4" | "5"', "data.url": "url", "data.tab": TAB } },
  "Multi-Button CTA #2": {
    controls: {
      "styles.quantity": '"4" | "5"',
      "styles.bgColor": COLOR,
      "styles.hoverColor": COLOR,
      "data.buttonInput<n>": "string",
      "data.customUrl<n>": "url",
      "data.openLink<n>": TAB,
    },
  },
  "Multi-Button CTA #3": {
    controls: { url: "url", tab: TAB, count: "number of buttons", bgColor: COLOR, hoverColor: COLOR },
  },
  "Multi-Button CTA #4": { controls: { url: "url", tab: TAB, bgOptionColor: COLOR, bgMainColor: COLOR } },
  "Multi-Step Form": {
    attribute: "data-multi-step-form-data",
    encoding: ENCODINGS.unescaped,
    controls: {
      formTemplate: '"dark" | "white"',
      formDescription: HTML,
      formTextColor: COLOR,
      formBackgroundColor: COLOR,
      formBorderColor: COLOR,
      inputButtonTextColor: COLOR,
      inputButtonBackgroundColor: COLOR,
      inputButtonBorderColor: COLOR,
      formMaxWidth: "css length, e.g. 380px",
      formPadding: "css length",
      "pages[]": "object[]",
    },
  },
  "Multi-Step Quiz #1": {
    attribute: "data-multi-step-quiz-data",
    encoding: ENCODINGS.htmlEscapedJson,
    controls: {
      "questions[]": "object[]",
      redirectUrl: "url",
      redirectAction: '"sameTab" | "newTab"',
    },
  },
  "Multi-Step Quiz #2": {
    attribute: "data-multi-step-quiz-data",
    encoding: ENCODINGS.json,
    nested: { fields: ["content"], expression: "btoa(html)" },
    controls: {
      "questions[]": "object[]",
      redirectUrl: "url",
      redirectAction: '"sameTab" | "newTab"',
      "data-no-loader": '"true" | omitted',
    },
  },
  "Multi-Step Quiz #3": {
    attribute: "data-multi-step-quiz-data",
    encoding: ENCODINGS.json,
    nested: { fields: ["content"], expression: "btoa(encodeURIComponent(html))" },
    controls: {
      imageID: "media id",
      imageURL: "url",
      primaryColor: COLOR,
      headline: HTML,
      "questions[]": "object[]",
    },
  },
  "Multi-Step Quiz #4": {
    attribute: "data-multi-step-quiz-data",
    encoding: ENCODINGS.json,
    nested: { fields: ["content"], expression: "btoa(encodeURIComponent(html))" },
    controls: {
      imageID: "media id",
      imageURL: "url",
      primaryColor: COLOR,
      questionsTree: "branching tree",
    },
  },
  "Newsletter Banner": {
    attribute: "data-newsletter-banner-data",
    encoding: ENCODINGS.escaped,
    controls: {
      "state1.content": HTML,
      "state1.emailPlaceholder": "string",
      "state1.buttonLabel": "string",
      "state1.buttonSize": PX,
      "state1.buttonBgColor": COLOR,
      "state1.buttonLabelColor": COLOR,
      "state1.disclaimerText": HTML,
      "state2.content": HTML,
      "state2.confirmationMessage": HTML,
    },
  },
  "Numbered Steps": {
    attribute: "data-numbered-steps-data",
    encoding: ENCODINGS.escaped,
    controls: { "steps[]": "object[]" },
  },
  "Ringba Phone Number": {
    attribute: "data-ringba-phone-number",
    encoding: ENCODINGS.json,
    controls: { ringbaPhoneNumber: "ringba number id", buttonType: '"button" | "link-with-button"' },
  },
  "Select Age Button (With Icons)": { controls: { url: "url", tab: TAB, color: "colour keyword, e.g. green" } },
  "Select Mileage Button": { controls: { url: "url", tab: TAB, color: "colour keyword, e.g. green" } },
  "Skinny Age Buttons": {
    controls: { url: "url", tab: TAB, count: '"3" | "4"', bgColor: COLOR, hoverColor: COLOR },
  },
  "Small Text Box": {
    attribute: "data-small-text-box-data",
    encoding: ENCODINGS.unescaped,
    controls: {
      text: "string",
      textColor: COLOR,
      bgColor: COLOR,
      borderColor: COLOR,
      borderWidth: PX,
      fontSize: PX,
      alignment: ALIGNMENT,
    },
  },
  "Social Proof": {
    attribute: "data-social-proof-data",
    encoding: ENCODINGS.escaped,
    controls: {
      reviewText: HTML,
      name: "string",
      starRating: "1 | 2 | 3 | 4 | 5",
      photo: "media object",
      photoOutline: COLOR,
      backgroundColor: COLOR,
      textColor: COLOR,
    },
  },
  "Social Proof V2": {
    attribute: "data-social-proof-v2-data",
    encoding: ENCODINGS.escaped,
    controls: {
      reviewText: HTML,
      imageID: "media id",
      imageURL: "url",
      imageSize: PX,
      enableOutline: "boolean",
      imageOutline: COLOR,
      enableBackground: "boolean",
      backgroundColor: COLOR,
      alignment: ALIGNMENT,
      layout: '"stacked" | "side-by-side"',
      borderRadius: PX,
      enableMaxWidth: "boolean",
      maxWidth: PX,
    },
  },
  "Spin wheel": {
    attribute: "data-spin-wheel-data",
    encoding: ENCODINGS.unescaped,
    controls: { message: HTML, buttonText: "string", buttonLink: "url", redirectOnClick: "boolean" },
  },
  "Text Highlight Box": { controls: { color: COLOR } },
  "Text Underline": {
    attribute: "data-text-underline-data",
    encoding: ENCODINGS.unescaped,
    controls: { text: HTML, lineColor: COLOR, lineWeight: PX, lineLength: PX, alignment: ALIGNMENT },
  },
  Timer: { controls: { quantity: "number (seconds)" } },
  Toast: {
    attribute: "data-toast-data",
    encoding: ENCODINGS.json,
    controls: {
      "toasts[].imageID": "media id",
      "toasts[].imageURL": "url",
      "toasts[].bodyText": HTML,
      "toasts[].timeText": "string",
      "data-initial-time": "number (ms)",
    },
  },
  "Two-Button CTA": {
    controls: { url: "url", tab: TAB, form: '"squared" | "rounded"', bgColor: COLOR, borderColor: COLOR },
  },
  "US States CTA": { controls: { url: "url", tab: TAB, type: "colour keyword, e.g. green" } },
  "Video Embed / Custom HTML": { controls: { html: "raw html or embed code" } },
  "Warning Banner": {
    controls: {
      labelText: "string",
      contentText: HTML,
      labelBgColor: COLOR,
      contentBgColor: COLOR,
      textColor: COLOR,
    },
  },
};

/**
 * Two base64 conventions are in use across the config dialogs, and a component can only parse
 * back the one its own dialog writes, so each sample has to use the matching encoder.
 */
const encodeExample = data => window.btoa(escape(encodeURIComponent(JSON.stringify(data))));
const encodeExampleUnescaped = data => window.btoa(unescape(encodeURIComponent(JSON.stringify(data))));

/**
 * Components that redirect on their own (the quizzes when answered, the spin wheel) set
 * `window.location`, which a preview frame resolves against the dashboard's own URL. The samples
 * point those at an inert link so finishing a quiz in the library goes nowhere.
 */
const INERT_LINK = "javascript:void(0)";

const encodeQuizContent = html => window.btoa(encodeURIComponent(html));

// The chat components walk a message tree, so the sample is a short scripted conversation.
const exampleLiveChat = () => ({
  botName: "Mary",
  botMessageBackgroundColor: "#f0f0f0",
  actionItemBackgroundColor: "#007bee",
  userMessageBackgroundColor: "#007bee",
  userMessageTextColor: "#FFFFFF",
  animatedButton: false,
  theme: "imessage",
  messages: [
    {
      messageType: "message",
      content: "<p>Hi! I can check in about a minute whether you qualify for the allowance card.</p>",
      delay: 800,
      human: false,
      children: [
        {
          messageType: "message",
          content: "<p>Are you over 64?</p>",
          delay: 800,
          human: false,
          children: [
            {
              messageType: "action",
              delay: 500,
              children: [
                { messageType: "actionItem", text: "Yes", value: "yes", children: [] },
                { messageType: "actionItem", text: "No", value: "no", children: [] },
              ],
            },
          ],
        },
      ],
    },
  ],
});

/**
 * Sample configuration for the elements the page renders as Vue components. Written onto the
 * wrapper exactly as a config dialog would, so `serializeDynamicComponents` turns it into the
 * same component tag a saved page carries and the library can render a working example instead
 * of the editor's placeholder image. `selector` targets an inner node for the few serializers
 * that read their config from there. Elements missing here keep the placeholder.
 */
const EXAMPLE_DATA = {
  "Age Quiz": () => ({
    attributes: {
      "data-age-input-data": encodeExample({
        ageInputType: "age",
        phoneNumber: "",
        showSpapQuestion: false,
        audioEnabled: false,
        questions: [],
      }),
    },
  }),
  // The serializer reads the type off the markup, so the element needs no sample config.
  "Carrier Names (OTC)": () => ({}),
  "Carrier Names (Part B)": () => ({}),
  // The bottom bar shows itself only when the page detects a mobile user agent, so a desktop
  // browser cannot render an example of it at any width.
  "Comments Widget": () => ({
    selector: '[data-tiny-mce-component="landercomments"]',
    attributes: {
      "data-comments-widget-data": JSON.stringify({
        1: {
          author: { name: "Karen T.", profilePicture: { imgURL: "", contentUrl: "" } },
          state: "Ohio",
          comment: "Took two minutes and my card arrived the following week.",
          date: "2 hours ago",
          likes: 14,
          liked: false,
        },
        2: {
          author: { name: "Robert P.", profilePicture: { imgURL: "", contentUrl: "" } },
          state: "Texas",
          comment: "Wish I had checked sooner - I qualified for the full amount.",
          date: "5 hours ago",
          likes: 6,
          liked: false,
        },
      }),
    },
  }),
  Counter: () => ({}),
  // These bake their settings into inline styles, so without the attribute the pasted element
  // looks right but the editor has nothing to reopen it with.
  "Buttons With Text-Box": () => ({
    attributes: { "data-buttons-with-text-box-data": window.btoa(JSON.stringify(BUTTONS_WITH_TEXT_BOX_OPTIONS)) },
  }),
  Countdown: () => ({
    attributes: { "data-countdown-data": encodeExampleUnescaped(countdownOptions()) },
  }),
  "Custom Button": () => ({
    attributes: { "data-custom-button-data": encodeExampleUnescaped(CUSTOM_BUTTON_OPTIONS) },
  }),
  "Dynamic Buttons": () => ({
    attributes: { "data-dynamic-buttons-data": encodeExample(DYNAMIC_BUTTONS_OPTIONS) },
  }),
  "Small Text Box": () => ({
    attributes: { "data-small-text-box-data": encodeExampleUnescaped(SMALL_TEXT_BOX_OPTIONS) },
  }),
  "Text Underline": () => ({
    attributes: { "data-text-underline-data": encodeExampleUnescaped(TEXT_UNDERLINE_OPTIONS) },
  }),
  "Live Chat": () => ({
    attributes: { "data-live-chat-data": encodeExampleUnescaped(exampleLiveChat()) },
  }),
  "Live Chat - Auto": () => ({
    attributes: { "data-live-chat-auto-data": encodeExampleUnescaped(exampleLiveChat()) },
  }),
  "Live Chat V2": () => ({
    attributes: { "data-live-chat-data": encodeExampleUnescaped(exampleLiveChat()) },
  }),
  "Live Chat (MP4)": () => ({
    attributes: {
      "data-live-chat-mp4-data": encodeExampleUnescaped({
        ...LIVE_CHAT_MP4_ROOT_DEFAULTS,
        ...exampleLiveChat(),
      }),
    },
  }),
  "Live Chat (MP4) V2": () => ({
    attributes: {
      "data-live-chat-mp4-data": encodeExampleUnescaped({
        ...LIVE_CHAT_MP4_ROOT_DEFAULTS,
        ...exampleLiveChat(),
      }),
    },
  }),
  "Multi-Step Form": () => ({
    attributes: {
      "data-multi-step-form-data": encodeExampleUnescaped({
        formTemplate: "dark",
        formMaxWidth: "380px",
        pages: [
          {
            pageType: "page",
            content: "<h3>Do you own your home?</h3>",
            children: [
              { pageType: "action", text: "Yes", children: [] },
              { pageType: "action", text: "No", children: [] },
            ],
          },
        ],
      }),
    },
  }),
  "Image Banner": () => ({
    attributes: {
      "data-image-banner-data": encodeExampleUnescaped({
        desktopGridLayout: "3x2",
        desktopCustomLayout: "",
        mobileGridLayout: "3x2",
        mobileCustomLayout: "",
        // The component skips any banner without an image, so the sample points at one the
        // page itself serves.
        imageBanners: Array.from({ length: 6 }, (unused, index) => ({
          imageID: "",
          imageURL: `${process.env.VUE_APP_API}/images/portraits/group_1/${(index % 5) + 1}.png`,
          headline: "How Senior Homeowners Are Upgrading To New Windows For Little To No Cost",
          subHeadline: "New Windows For Seniors",
          url: "#",
        })),
      }),
    },
  }),
  "Live Claim Feed": () => ({
    attributes: {
      "data-live-claim-feed-data": encodeExample({
        feedTitle: "Live Claim Feed",
        turnRate: 3000,
        numberOfLines: 5,
        numberOfSections: 10,
        numberOfUsers: 5,
        horizontalTexts: { name: "Name", state: "State", benefitAmount: "Benefit Amount" },
        verticalTexts: { name: "Name", state: "State", benefitAmount: "Benefit Amount" },
        claimFeedUsers: [
          { name: "Maria D.", state: "CA", benefitAmount: "$24,000" },
          { name: "Olivia J.", state: "NJ", benefitAmount: "$18,500" },
          { name: "Robert P.", state: "TX", benefitAmount: "$31,200" },
          { name: "Susan K.", state: "FL", benefitAmount: "$12,750" },
          { name: "James W.", state: "OH", benefitAmount: "$27,400" },
        ],
      }),
    },
  }),
  "Multi-Step Quiz #1": () => ({
    selector: '[data-tiny-mce-component="multistepquiz"]',
    attributes: {
      "data-multi-step-quiz-data": JSON.stringify([
        {
          question: "Are you over 64?",
          answers: [{ text: "Yes" }, { text: "No" }],
          url: { redirectAction: "sameTab", link: INERT_LINK },
        },
        {
          question: "Do you have Medicare Parts A and B?",
          answers: [{ text: "Yes" }, { text: "Not sure" }],
          url: { redirectAction: "sameTab", link: INERT_LINK },
        },
      ]),
    },
  }),
  "Multi-Step Quiz #2": () => ({
    attributes: {
      "data-multi-step-quiz-data": JSON.stringify({
        questions: [
          {
            // The parse reviver decodes every `content` key, answers included, with a bare atob.
            content: window.btoa("<h3>Are you over 64?</h3>"),
            answers: [
              { content: window.btoa("Yes"), destination: "success" },
              { content: window.btoa("No"), destination: "success" },
            ],
          },
        ],
        destinations: { success: "<h3>Good news - you qualify.</h3>" },
      }),
      "data-no-loader": "true",
    },
  }),
  "Multi-Step Quiz #3": () => ({
    attributes: {
      // V3 reads its config from a `quizData` key rather than the root object.
      "data-multi-step-quiz-data": JSON.stringify({
        quizData: {
          primaryColor: "#0D6EFD",
          imageURL: `${process.env.VUE_APP_API}/images/portraits/group_1/1.png`,
          // V3 and V4 decode every `content` key with atob plus decodeURIComponent.
          headline: { content: encodeQuizContent("<h3>Check your eligibility</h3>") },
          questions: [
            {
              content: encodeQuizContent("<h3>Are you over 64?</h3>"),
              answers: [
                { content: encodeQuizContent("Yes"), destination: "success" },
                { content: encodeQuizContent("No"), destination: "success" },
              ],
            },
          ],
          destinations: { success: { content: encodeQuizContent("<h3>Good news - you qualify.</h3>") } },
        },
      }),
    },
  }),
  "Multi-Step Quiz #4": () => ({
    attributes: {
      "data-multi-step-quiz-data": JSON.stringify({
        primaryColor: "#0D6EFD",
        imageURL: `${process.env.VUE_APP_API}/images/portraits/group_1/1.png`,
        headline: { content: encodeQuizContent("<h3>Check your eligibility</h3>") },
        // V4 walks a tree whose nodes keep their content under `data`.
        questionsTree: {
          root: {
            data: { content: encodeQuizContent("<h3>Are you over 64?</h3>") },
            children: [
              {
                data: { content: encodeQuizContent("Yes") },
                children: [{ data: { content: encodeQuizContent("<h3>Good news - you qualify.</h3>") }, children: [] }],
              },
              {
                data: { content: encodeQuizContent("No") },
                children: [{ data: { content: encodeQuizContent("<h3>Thanks for checking.</h3>") }, children: [] }],
              },
            ],
          },
        },
      }),
    },
  }),
  "Newsletter Banner": () => ({
    attributes: {
      "data-newsletter-banner-data": encodeExample({
        state1: {
          content:
            '<h3 style="font-size: 32px;">Smart ideas, straight to your inbox.</h3><p class="newsletter-banner-content" style="font-size: 18px;">A short, useful read every Thursday.</p>',
          emailPlaceholder: "your@email.com",
          buttonLabel: "Subscribe",
          buttonSize: 14,
          buttonBgColor: "#355aff",
          buttonLabelColor: "#ffffff",
          disclaimerText: '<p class="newsletter-banner-content" style="font-size: 14px;">Unsubscribe anytime.</p>',
        },
        state2: {
          content: '<h3 style="font-size: 32px;">You\'re subscribed!</h3>',
          confirmationMessage: '<p class="newsletter-banner-content">You\'re on the list.</p>',
        },
      }),
    },
  }),
  "Numbered Steps": () => ({
    attributes: {
      "data-numbered-steps-data": encodeExample({
        steps: [{ text: "Select your age below" }, { text: "Answer a few questions" }, { text: "See if you qualify" }],
      }),
    },
  }),
  "Social Proof": () => ({
    attributes: {
      "data-social-proof-data": encodeExample({
        starRating: 5,
        reviewText: "I waited too long, but finally got $25,000 in final expense life insurance.",
        name: "Susan",
        photo: null,
        photoOutline: "#37A9FC",
        backgroundColor: "#F0F9FF",
        textColor: "#1C1C1C",
      }),
    },
  }),
  "Social Proof V2": () => ({
    attributes: {
      "data-social-proof-v2-data": encodeExample({
        reviewText: "I had no idea I qualified. The whole thing took two minutes.",
        imageID: "",
        imageURL: "",
        enableOutline: false,
        imageOutline: "#37A9FC",
        enableBackground: false,
        backgroundColor: "#F0F9FF",
        alignment: "center",
        layout: "stacked",
        imageSize: 64,
        borderRadius: 12,
        enableMaxWidth: false,
        maxWidth: 800,
      }),
    },
  }),
  "Spin wheel": () => ({
    attributes: {
      "data-spin-wheel-data": encodeExampleUnescaped({
        message: "<p>Spin to reveal the benefit you qualify for.</p>",
        buttonText: "See My Benefit",
        buttonLink: INERT_LINK,
        redirectOnClick: false,
      }),
    },
  }),
  Timer: () => ({}),
  Toast: () => ({
    attributes: {
      "data-toast-data": JSON.stringify([
        {
          id: 1,
          imageID: "",
          imageURL: "",
          bodyText: "Velia E. from Niota, TN qualified for a <strong>$500</strong> allowance card.",
          timeText: "10 seconds ago",
        },
      ]),
      "data-initial-time": JSON.stringify("1000"),
    },
  }),
  "Zip Code": () => ({}),
};

/**
 * Wraps the element markup the way the editor stores it, so the result can be pasted straight
 * into the Source code view. The click-to-remove div the editor shows on top is left out: it is
 * editor chrome that gets stripped on save and re-added on load.
 */
const buildEditorSnippet = (name, markup) => {
  const { id = "", inline = false, unwrapped = false } = EDITOR_WRAPPERS[name] ?? {};
  const uuid = uuidv4();
  const wrapped = unwrapped
    ? markup
    : inline
    ? getSpanShortcodeWrapper(uuid, "", markup, id, false)
    : getShortcodeWrapper(uuid, "", markup, id);

  return wrapped.replace(/\n\s*\n/g, "\n").trim();
};

/**
 * The same wrapper carrying sample config, which `serializeDynamicComponents` then turns into the
 * page's own component tag. Empty when the element has no sample config to render from.
 */
/**
 * The sample config read back out of the example markup, so what the library hands to an AI is
 * the shape the element really carries - a chat's message tree, a quiz's questions - rather than
 * a one-line summary of it. Decoding what was emitted, instead of keeping a second copy, is what
 * keeps the two from drifting. Three base64 conventions are in use across the dialogs, so each is
 * tried in turn; some templates carry the attribute themselves, so the plain snippet is read too.
 */
const decodeExampleConfig = (name, snippet) => {
  const { attribute, encoding } = ELEMENT_CONTROLS[name] ?? {};

  if (!attribute || !snippet) return null;

  const raw = parseToHtml(snippet).querySelector(`[${attribute}]`)?.getAttribute(attribute);

  if (!raw) return null;

  const decoders = {
    [ENCODINGS.escaped]: () => decodeURIComponent(unescape(window.atob(raw))),
    [ENCODINGS.unescaped]: () => decodeURIComponent(escape(window.atob(raw))),
    [ENCODINGS.base64]: () => window.atob(raw),
    [ENCODINGS.json]: () => raw,
    [ENCODINGS.htmlEscapedJson]: () => decodeHtmlSymbols(raw),
  };

  // This runs inside the view's `data()`, so a payload that cannot be read must not take the
  // whole page down with it - the element simply ships without its sample config.
  try {
    return JSON.parse(decoders[encoding]());
  } catch {
    return null;
  }
};

const buildExampleSnippet = (name, markup) => {
  const example = EXAMPLE_DATA[name];

  if (!example) return "";

  const { attributes = {}, selector = "" } = example();
  const wrapper = parseToHtml(buildEditorSnippet(name, markup)).body.firstElementChild;
  // Some serializers read the config off an inner node rather than the wrapper.
  const target = selector ? wrapper.querySelector(selector) : wrapper;

  Object.entries(attributes).forEach(([attribute, value]) => target.setAttribute(attribute, value));

  return wrapper.outerHTML;
};

const PREVIEW_URL = "#";
const PREVIEW_TAB = "_self";
// Every colour in a preview is the element's own dialog default, so the library shows each
// element exactly as it arrives when you insert it. This green is the shared default across a
// dozen of the button dialogs; anything that differs is written inline next to its element.
const DEFAULT_GREEN = "#28b02b";
const PREVIEW_PHONE = "(888) 555-0123";
const PREVIEW_LINK = { url: PREVIEW_URL, tab: PREVIEW_TAB, type: "green" };
const PREVIEW_CTA_TEXT = "See If You Qualify";

const previewButton = (overrides = {}) => ({ ...PREVIEW_LINK, text: PREVIEW_CTA_TEXT, ...overrides });

const daysFromNow = days => new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

/**
 * Options shared between an element's preview markup and the config attribute its example writes,
 * so the two always describe the same element. These elements bake their settings into inline
 * styles, which renders correctly but leaves the editor nothing to reopen without the attribute.
 */
const CUSTOM_BUTTON_OPTIONS = {
  text: PREVIEW_CTA_TEXT,
  enableSecondLine: false,
  secondaryText: "",
  secondaryFontSize: "14",
  secondaryTextColor: "#ffffff",
  lineGap: "0",
  enableIcon: false,
  icon: "check",
  customIconId: "",
  customIconSrc: "",
  iconGap: "8",
  iconSize: "22",
  iconColor: "#ffffff",
  url: PREVIEW_URL,
  openLink: PREVIEW_TAB,
  bgColor: DEFAULT_GREEN,
  textColor: "#ffffff",
  enableHover: false,
  hoverColor: DEFAULT_GREEN,
  enableBorder: false,
  borderColor: DEFAULT_GREEN,
  borderWidth: "2",
  borderRadius: "40",
  fontFamily: "",
  fontSize: "22",
  enableNoWrap: false,
  paddingY: "20",
  paddingX: "32",
  enableFullWidth: false,
  width: "400",
  enablePulse: false,
  enableAd: false,
  enable3d: false,
  shadowX: "6",
  shadowY: "6",
  shadowBlur: "0",
  shadowColor: "#1a7d1e",
};

const SMALL_TEXT_BOX_OPTIONS = {
  text: "BENEFIT #1",
  textColor: "#2454C7",
  borderColor: "#2454C7",
  bgColor: "#FFFFFF",
  fontSize: "12",
  borderWidth: "1.5",
  alignment: "left",
};

const TEXT_UNDERLINE_OPTIONS = {
  text: '<p style="font-size: 12px; color: #2454C7;">BENEFIT #1</p>',
  alignment: "left",
  lineColor: "#2454C7",
  lineWeight: 2,
  lineLength: 140,
};

const DYNAMIC_BUTTONS_OPTIONS = {
  globalLink: PREVIEW_URL,
  mobileColumns: 1,
  desktopColumns: 2,
  buttons: [{ text: "Yes, I Qualify" }, { text: "Show Me Plans" }],
};

/**
 * The two buttons its dialog starts with. A `textBox` entry is deliberately left out: the
 * template walks the buttons twice, so such an entry renders both as a button and as the panel
 * below it, printing the same sentence twice.
 */
const BUTTONS_WITH_TEXT_BOX_OPTIONS = {
  primaryColor: "#28B02B",
  buttons: [
    { type: "url", title: "First button", url: PREVIEW_URL, tab: PREVIEW_TAB, openIn: "_self", text: "Yes" },
    { type: "url", title: "Second button", url: PREVIEW_URL, tab: PREVIEW_TAB, openIn: "_self", text: "No" },
  ],
};

const countdownOptions = () => ({ timerColor: "#1C42BE", selectedDate: daysFromNow(30) });

// The highlight boxes ship empty because the user types inside them in the editor, so the
// preview drops sample copy in before the closing tag.
const withSampleCopy = template => {
  const copy = "<p>Most seniors never claim the extra benefits they qualify for. Checking takes under a minute.</p>";
  const closingTagIndex = template.lastIndexOf("</div>");

  return template.slice(0, closingTagIndex) + copy + template.slice(closingTagIndex);
};

/**
 * The shortcode picker stores a bare template as each element's identity token, so most of the
 * values it carries are empty shells. Previews therefore call the same template functions again
 * with representative data. `staticPreview` marks the elements whose editor markup is only a
 * placeholder image or a token the API swaps out when the page renders.
 */
const ELEMENT_METADATA = {
  "Button CTA #1": {
    slug: "button_cta_1",
    category: "cta",
    tags: ["button", "primary", "arrow"],
    preview: () => getCTATemplate(previewButton({ bgColor: DEFAULT_GREEN })),
  },
  "Button CTA #2": {
    slug: "button_cta_2",
    category: "cta",
    tags: ["button", "primary", "arrow"],
    preview: () => getCTAIndyTemplate(previewButton()),
  },
  "Button CTA #3": {
    slug: "button_cta_3",
    category: "cta",
    tags: ["button", "compact"],
    preview: () => getShortCTATemplate(previewButton({ bgColor: DEFAULT_GREEN })),
  },
  "Button CTA #4": {
    slug: "button_cta_4",
    category: "cta",
    tags: ["button", "compact", "custom-colour"],
    preview: () => getShortCTA4Template(previewButton({ bgColor: DEFAULT_GREEN, textColor: "#ffffff" })),
  },
  "Custom Button": {
    slug: "custom_button",
    category: "cta",
    tags: ["button", "configurable", "presets"],
    // The template renders a pre-computed view rather than dialog data, so the preview goes
    // through the config module's own builder instead of a hand-written view object.
    preview: () => getCustomButtonTemplate(getCustomButtonView(CUSTOM_BUTTON_OPTIONS)),
  },
  "Button CTA (With Ad)": {
    slug: "button_cta_with_ad",
    category: "cta",
    tags: ["button", "primary", "disclosure"],
    preview: () => getCTATemplateWidthAd(previewButton()),
  },
  "CTA Outer Button": {
    slug: "cta_outer_button",
    category: "cta",
    tags: ["button", "outlined"],
    preview: () => getCTAOuterTemplate(previewButton()),
  },
  "Two-Button CTA": {
    slug: "two_button_cta",
    category: "cta",
    tags: ["button", "yes-no", "qualifier"],
    preview: () => getMultipleCTATemplate({ ...PREVIEW_LINK, bgColor: "#30c632", borderColor: "#2bab2e" }),
  },
  "Multi-Button CTA #1": {
    slug: "multi_button_cta_1",
    category: "cta",
    tags: ["buttons", "offers", "list"],
    preview: () => getMultiButtonCta({ quantity: "4", data: { url: PREVIEW_URL, tab: PREVIEW_TAB } }),
  },
  "Multi-Button CTA #2": {
    slug: "multi_button_cta_2",
    category: "cta",
    tags: ["buttons", "offers", "numbered"],
    preview: () =>
      getDynamicColorMultiButtonCta({
        styles: { quantity: "4", bgColor: "#4A8269", hoverColor: "#194147" },
        data: {
          customUrl1: PREVIEW_URL,
          customUrl2: PREVIEW_URL,
          customUrl3: PREVIEW_URL,
          customUrl4: PREVIEW_URL,
          openLink1: PREVIEW_TAB,
          openLink2: PREVIEW_TAB,
          openLink3: PREVIEW_TAB,
          openLink4: PREVIEW_TAB,
          buttonInput1: "Check Grocery Allowance",
          buttonInput2: "Check Utility Help",
          buttonInput3: "Check Dental Coverage",
          buttonInput4: "Check Transport Benefit",
        },
      }),
  },
  "Multi-Button CTA #3": {
    slug: "multi_button_cta_3",
    category: "cta",
    tags: ["buttons", "list", "custom-colour"],
    preview: () =>
      getMultiButtonCtaV3Template({
        ...PREVIEW_LINK,
        count: 3,
        bgColor: DEFAULT_GREEN,
        hoverColor: DEFAULT_GREEN,
      }),
  },
  "Multi-Button CTA #4": {
    slug: "multi_button_cta_4",
    category: "cta",
    tags: ["buttons", "icons", "benefits"],
    preview: () => getMultiButtonCtaV4Template({ ...PREVIEW_LINK, bgOptionColor: "#2377F8", bgMainColor: "#28B02B" }),
  },
  "Multi Button CTA with Images": {
    slug: "multi_button_cta_with_images",
    category: "cta",
    tags: ["buttons", "images", "offers"],
    preview: () =>
      getMultiButtonCtaWithImages({
        quantity: 3,
        data: {
          customUrl: PREVIEW_URL,
          openLink: PREVIEW_TAB,
          boxInput1: "Unsold SUV Clearance Pricing",
          boxInput2: "Senior Grocery Allowance",
          boxInput3: "Dental Plans From $0",
          imgAlt1: "offer",
          imgAlt2: "offer",
          imgAlt3: "offer",
          // The template reads the thumbnail off a media object, so a bare url renders nothing.
          imgSrc1: { value: `${process.env.VUE_APP_API}/images/portraits/group_1/1.png` },
          imgSrc2: { value: `${process.env.VUE_APP_API}/images/portraits/group_1/2.png` },
          imgSrc3: { value: `${process.env.VUE_APP_API}/images/portraits/group_1/3.png` },
        },
      }),
  },
  "Multi Button CTA (White)": {
    slug: "multi_button_cta_white",
    category: "cta",
    tags: ["buttons", "offers", "light"],
    preview: () => getWhiteMultiButtonCta({ quantity: "4", url: PREVIEW_URL, tab: PREVIEW_TAB }),
  },
  "Dynamic Buttons": {
    slug: "dynamic_buttons",
    category: "cta",
    tags: ["buttons", "grid", "custom-colour"],
    preview: () => getDynamicButtonsTemplate(DYNAMIC_BUTTONS_OPTIONS),
  },
  "Animated Button": {
    slug: "animated_button",
    category: "cta",
    tags: ["button", "animation", "attention"],
    preview: () =>
      getAnimatedButtonTemplate({
        ...PREVIEW_LINK,
        text: PREVIEW_CTA_TEXT,
        bgColor: "#004b75",
        hoverColor: "#003452",
        type: "link",
      }),
  },
  "Money CTA": {
    slug: "money_cta",
    category: "cta",
    tags: ["buttons", "qualifier", "money"],
    preview: () => getMoneyCTATemplate(),
  },
  "Buttons With Text-Box": {
    slug: "buttons_with_text_box",
    category: "cta",
    tags: ["buttons", "text", "explainer"],
    preview: () => getButtonsWithTextBoxTemplate(BUTTONS_WITH_TEXT_BOX_OPTIONS),
  },

  "Call CTA #1": {
    slug: "call_cta_1",
    category: "call",
    tags: ["phone", "call", "button"],
    preview: () => getCTAPhoneTemplate({ bgColor: DEFAULT_GREEN, number: PREVIEW_PHONE, text: "Call Now" }),
  },
  "Call CTA #2": {
    slug: "call_cta_2",
    category: "call",
    tags: ["phone", "call", "number"],
    preview: () =>
      getCtaButtonNumberTemplate({
        hrefType: "tel:",
        bgColor: "#1CB91F",
        borderColor: "#1DA220",
        url: PREVIEW_PHONE,
        number: PREVIEW_PHONE,
        text: "TTY 711 - Call 24/7",
      }),
  },
  "Ringba Phone Number": {
    slug: "ringba_phone_number",
    category: "call",
    tags: ["phone", "call", "ringba", "dynamic"],
    staticPreview: true,
    preview: () =>
      getRingbaPhoneNumberTemplate({
        buttonType: "link-with-button",
        ringbaPhoneNumber: { text: PREVIEW_PHONE, value: PREVIEW_PHONE },
      }),
  },

  "Age CTA #1": {
    slug: "age_cta_1",
    category: "qualifier",
    tags: ["age", "qualifier", "buttons"],
    preview: () => getSelectAgeTemplate(PREVIEW_LINK),
  },
  "Age CTA #2": {
    slug: "age_cta_2",
    category: "qualifier",
    tags: ["age", "qualifier", "row"],
    preview: () =>
      getSelectAgeInOneRowTemplate({
        ...PREVIEW_LINK,
        count: "4",
        buttonSpacing: "23",
        cornerRadius: "8",
        bgColor: DEFAULT_GREEN,
        hoverColor: DEFAULT_GREEN,
      }),
  },
  "Age CTA #3": {
    slug: "age_cta_3",
    category: "qualifier",
    tags: ["age", "qualifier", "shadow"],
    preview: () =>
      getAgeCTA3Template({
        ...PREVIEW_LINK,
        count: "4",
        cornerRadius: "8",
        bgColor: DEFAULT_GREEN,
        hoverColor: DEFAULT_GREEN,
      }),
  },
  "Age CTA #4": {
    slug: "age_cta_4",
    category: "qualifier",
    tags: ["age", "qualifier", "portraits"],
    preview: () => getAgeCTA4Template({ ...PREVIEW_LINK, version: 1 }),
  },
  "Select Age Button (With Icons)": {
    slug: "select_age_icons",
    category: "qualifier",
    tags: ["age", "gender", "icons"],
    preview: () => getSelectAgeIconsTemplate({ ...PREVIEW_LINK, color: "green" }),
  },
  "Skinny Age Buttons": {
    slug: "skinny_age_buttons",
    category: "qualifier",
    tags: ["age", "qualifier", "compact"],
    preview: () =>
      getSkinnyAgeButtonsTemplate({
        ...PREVIEW_LINK,
        count: "4",
        bgColor: DEFAULT_GREEN,
        hoverColor: DEFAULT_GREEN,
      }),
  },
  "Age Gif": {
    slug: "age_gif",
    category: "qualifier",
    tags: ["age", "qualifier", "animation"],
    preview: () => getGifAgeTemplate(PREVIEW_LINK),
  },
  "Select Mileage Button": {
    slug: "select_mileage",
    category: "qualifier",
    tags: ["mileage", "qualifier", "buttons"],
    preview: () => getMileageTemplate({ ...PREVIEW_LINK, color: "green" }),
  },
  "US States CTA": {
    slug: "us_states_cta",
    category: "qualifier",
    tags: ["location", "states", "qualifier"],
    preview: () => getUsStatesShortcodeTemplate(states.slice(0, 9), PREVIEW_LINK),
  },
  "Zip Code": {
    slug: "zip_code",
    category: "qualifier",
    tags: ["location", "zip", "input"],
    staticPreview: true,
    preview: () => getZipCodeTemplate(),
  },
  "Carrier Names (OTC)": {
    slug: "carrier_names_otc",
    category: "qualifier",
    tags: ["carriers", "dynamic", "otc"],
    staticPreview: true,
    preview: () => getCarrierNamesOtcTemplate(),
  },
  "Carrier Names (Part B)": {
    slug: "carrier_names_part_b",
    category: "qualifier",
    tags: ["carriers", "dynamic", "part-b"],
    staticPreview: true,
    preview: () => getCarrierNamesPartBTemplate(),
  },

  "Multi-Step Quiz #1": {
    slug: "multi_step_quiz_1",
    category: "quizzes",
    tags: ["quiz", "steps", "qualifier"],
    staticPreview: true,
    preview: pickerValue => pickerValue,
  },
  "Multi-Step Quiz #2": {
    slug: "multi_step_quiz_2",
    category: "quizzes",
    tags: ["quiz", "steps", "loader"],
    staticPreview: true,
    preview: pickerValue => pickerValue,
  },
  "Multi-Step Quiz #3": {
    slug: "multi_step_quiz_3",
    category: "quizzes",
    tags: ["quiz", "steps", "avatar"],
    staticPreview: true,
    preview: () => getMultiStepQuizV3Template(),
  },
  "Multi-Step Quiz #4": {
    slug: "multi_step_quiz_4",
    category: "quizzes",
    tags: ["quiz", "branching", "flow"],
    staticPreview: true,
    preview: () => getMultiStepQuizV4Template(),
  },
  "Age Quiz": {
    slug: "age_quiz",
    category: "quizzes",
    tags: ["quiz", "age", "input"],
    staticPreview: true,
    preview: () => getAgeInputQuizTemplate(),
  },
  "Spin wheel": {
    slug: "spin_wheel",
    category: "quizzes",
    tags: ["game", "prize", "interactive"],
    staticPreview: true,
    preview: () => getSpinWheelTemplate(),
  },

  "Multi-Step Form": {
    slug: "multi_step_form",
    category: "forms",
    tags: ["form", "steps", "lead"],
    staticPreview: true,
    preview: () => getMultiStepFormTemplate(),
  },
  "Contact Form": {
    slug: "contact_form",
    category: "forms",
    tags: ["form", "lead", "contact"],
    preview: () => getContactFormTemplate(),
  },
  "Contact Us Form": {
    slug: "contact_us_form",
    category: "forms",
    tags: ["form", "contact", "sidebar"],
    preview: () =>
      getContactUsFormTemplate({
        // The template defaults everything except the field labels.
        nameLabel: "Full Name",
        emailLabel: "Email Address",
        messageLabel: "Message",
        namePlaceholder: "",
        emailPlaceholder: "",
        messagePlaceholder: "Type your message here...",
      }),
  },
  "Email form": {
    slug: "email_form",
    category: "forms",
    tags: ["form", "email", "lead"],
    preview: () => getEmailFormTemplate(),
  },
  "Drip Form Widget": {
    slug: "drip_form",
    category: "forms",
    tags: ["form", "email", "drip"],
    preview: () =>
      getDripFormTemplate({
        title: "Get the benefits checklist",
        subtitle: "One email, no spam. Unsubscribe any time.",
        buttonText: "Send It To Me",
      }),
  },
  "Checkout Plan": {
    slug: "checkout_plan",
    category: "forms",
    tags: ["checkout", "plan", "pricing"],
    preview: () => getCheckoutPlanTemplate(),
  },
  "Checkout Form": {
    slug: "checkout_form",
    category: "forms",
    tags: ["checkout", "payment", "card"],
    preview: () => getCheckoutFormTemplate(),
  },

  "Live Chat": {
    slug: "live_chat",
    category: "live_chat",
    tags: ["chat", "conversation", "interactive"],
    staticPreview: true,
    preview: () => getLiveChatTemplate(),
  },
  "Live Chat V2": {
    slug: "live_chat_v2",
    category: "live_chat",
    tags: ["chat", "conversation", "interactive"],
    staticPreview: true,
    preview: () => getLiveChatV2Template(),
  },
  "Live Chat - Auto": {
    slug: "live_chat_auto",
    category: "live_chat",
    tags: ["chat", "automatic", "interactive"],
    staticPreview: true,
    preview: () => getLiveChatAutoTemplate(),
  },
  "Live Chat (MP4)": {
    slug: "live_chat_mp4",
    category: "live_chat",
    tags: ["chat", "video", "interactive"],
    staticPreview: true,
    preview: () => getLiveChatMp4Template(),
  },
  "Live Chat (MP4) V2": {
    slug: "live_chat_mp4_v2",
    category: "live_chat",
    tags: ["chat", "video", "interactive"],
    staticPreview: true,
    preview: () => getLiveChatMp4V2Template(),
  },
  "Livechat CTA #1": {
    slug: "livechat_cta_1",
    category: "live_chat",
    tags: ["chat", "button", "cta"],
    preview: () =>
      getLiveChatCTATemplate({
        url: PREVIEW_URL,
        text: "Chat With An Agent",
        bgColor: "#007bee",
        pulsingAnimation: true,
      }),
  },
  "Livechat CTA #2": {
    slug: "livechat_cta_2",
    category: "live_chat",
    tags: ["chat", "button", "two-line"],
    preview: () =>
      getLiveChatCTAV2Template({
        url: PREVIEW_URL,
        twoLine: true,
        line1: "Chat With An Agent",
        line2: "Average wait: 30 seconds",
        bgColor: DEFAULT_GREEN,
        textColor: "#ffffff",
        fontSize: "20",
        fontSize2: "14",
      }),
  },

  "Social Proof Element": {
    slug: "social_proof_element",
    category: "social_proof",
    tags: ["ratings", "reviews", "trust"],
    preview: () => getReviewRatingsTemplate(),
  },
  "Social Proof": {
    slug: "social_proof",
    category: "social_proof",
    tags: ["testimonial", "reviews", "trust"],
    staticPreview: true,
    preview: () => getSocialProofTemplate(),
  },
  "Social Proof V2": {
    slug: "social_proof_v2",
    category: "social_proof",
    tags: ["testimonial", "reviews", "trust"],
    staticPreview: true,
    preview: () => getSocialProofV2Template(),
  },
  "Comments Widget": {
    slug: "comments_widget",
    category: "social_proof",
    tags: ["comments", "engagement", "trust"],
    staticPreview: true,
    preview: () => getCommentsTemplate(),
  },
  "Team members widget": {
    slug: "team_members",
    category: "social_proof",
    tags: ["team", "authors", "trust"],
    preview: () => getTeamMembersTemplate(),
  },
  "Live Claim Feed": {
    slug: "live_claim_feed",
    category: "social_proof",
    tags: ["feed", "urgency", "trust"],
    staticPreview: true,
    preview: () => getLiveClaimFeedTemplate(),
  },
  Counter: {
    slug: "counter",
    category: "social_proof",
    tags: ["counter", "urgency", "dynamic"],
    staticPreview: true,
    preview: () => getCounterTemplate("1000"),
  },
  "Social Network Share Button": {
    slug: "social_sharer",
    category: "social_proof",
    tags: ["share", "social", "facebook"],
    preview: () => getSocialSharerTemplate(),
  },

  Toast: {
    slug: "toast",
    category: "banners",
    tags: ["notification", "urgency", "popup"],
    staticPreview: true,
    preview: () => getToastTemplate(),
  },
  "Bottom Bar": {
    slug: "bottom_bar",
    category: "banners",
    tags: ["sticky", "phone", "bar"],
    staticPreview: true,
    preview: () => getBottomBarTemplate(),
  },
  "Newsletter Banner": {
    slug: "newsletter_banner",
    category: "banners",
    tags: ["newsletter", "email", "signup"],
    staticPreview: true,
    preview: () => getNewsletterBannerTemplate(),
  },
  "Image Banner": {
    slug: "image_banner",
    category: "banners",
    tags: ["cards", "grid", "articles"],
    staticPreview: true,
    preview: () => getImageBannerTemplate(),
  },
  "Warning Banner": {
    slug: "warning_banner",
    category: "banners",
    tags: ["warning", "deadline", "urgency"],
    preview: () => getWarningBannerTemplate(),
  },

  "Blockquote #1": {
    slug: "blockquote_1",
    category: "social_proof",
    tags: ["quote", "testimonial", "stars"],
    preview: () => getQuoteBoxTemplate(),
  },
  "Blockquote #2": {
    slug: "blockquote_2",
    category: "social_proof",
    tags: ["quote", "callout"],
    preview: () => getQuoteTemplate(),
  },
  "Text Highlight Box": {
    slug: "text_highlight_box",
    category: "text",
    tags: ["callout", "highlight", "custom-colour"],
    preview: () => withSampleCopy(getDynamicHighlightBoxTemplate("#F8F9FA")),
  },
  "Yellow Highlight Box": {
    slug: "yellow_highlight_box",
    category: "text",
    tags: ["callout", "highlight", "yellow"],
    preview: () => withSampleCopy(getTextHighlightTemplate("info-highlight")),
  },
  "Blue Highlight Box": {
    slug: "blue_highlight_box",
    category: "text",
    tags: ["callout", "highlight", "blue"],
    preview: () => withSampleCopy(getTextHighlightTemplate("info-blue")),
  },
  "Small Text Box": {
    slug: "small_text_box",
    category: "text",
    tags: ["callout", "label", "inline"],
    preview: () => getSmallTextBoxTemplate(SMALL_TEXT_BOX_OPTIONS),
  },
  "Content Box": {
    slug: "content_box",
    category: "text",
    tags: ["box", "layout", "columns"],
    preview: () => getContentBoxTemplate(),
  },
  "Text Underline": {
    slug: "text_underline",
    category: "text",
    tags: ["heading", "underline", "accent"],
    preview: () => getTextUnderlineTemplate(TEXT_UNDERLINE_OPTIONS),
  },
  "Numbered Steps": {
    slug: "numbered_steps",
    category: "text",
    tags: ["steps", "how-it-works", "list"],
    staticPreview: true,
    preview: () => getNumberedStepsTemplate(),
  },
  "Headline #1": {
    slug: "headline_1",
    category: "text",
    tags: ["headline", "heading", "background"],
    preview: () =>
      getHeadlineWithBgTemplate({
        text: "Seniors In Your State May Qualify For A $500 Allowance",
        enableBorders: true,
        bordersColor: "#E6E6E6",
        enableBackgroundColor: true,
        bgColor: "#0543F0",
      }),
  },

  Timer: {
    slug: "timer",
    category: "utility",
    tags: ["countdown", "urgency", "inline"],
    staticPreview: true,
    preview: () => getTimerTemplate("2"),
  },
  Countdown: {
    slug: "countdown",
    category: "utility",
    tags: ["countdown", "deadline", "urgency"],
    preview: () => getCountdownTemplate(countdownOptions()),
  },
  "Video Embed / Custom HTML": {
    slug: "custom_html",
    category: "utility",
    tags: ["video", "embed", "html"],
    preview: () =>
      getShortcodeHTMLTemplate({
        html: "<p>Paste any embed code or custom HTML here - a video player, a partner widget, a tracking pixel.</p>",
      }),
  },
  "Content Split": {
    slug: "content_split",
    category: "utility",
    tags: ["geo", "targeting", "location"],
    staticPreview: true,
    preview: () => getContentSplitTemplate(),
  },
};

/**
 * Joins the picker's permission-filtered element list with the library metadata, so the library
 * only ever shows elements the current user can actually insert.
 */
export const getDesignElements = () =>
  shortcodesList.map(({ text, value }) => {
    const metadata = ELEMENT_METADATA[text];
    const category = metadata?.category ?? UNCATEGORIZED;
    const markup = metadata ? metadata.preview(value) : value;
    const {
      controls = {},
      attribute = "",
      encoding = "",
      nested = null,
      nodeTypes = null,
    } = ELEMENT_CONTROLS[text] ?? {};
    const example = buildExampleSnippet(text, markup);

    return {
      name: text,
      category,
      categoryLabel: i18n.t(`design_elements.categories.${category}`),
      description: metadata ? i18n.t(`design_elements.descriptions.${metadata.slug}`) : "",
      tags: metadata?.tags ?? [],
      staticPreview: metadata?.staticPreview ?? false,
      controls,
      configAttribute: attribute,
      configEncoding: encoding,
      nestedEncoding: nested,
      nodeTypes,
      markup,
      snippet: buildEditorSnippet(text, markup),
      example,
      exampleConfig: decodeExampleConfig(text, example || buildEditorSnippet(text, markup)),
    };
  });

export const getCategoriesWithCounts = elements =>
  ELEMENT_CATEGORIES.concat(UNCATEGORIZED)
    .map(id => ({
      id,
      label: i18n.t(`design_elements.categories.${id}`),
      count: elements.filter(element => element.category === id).length,
    }))
    .filter(category => category.count > 0);
